import React from 'react'
import { FormCheckboxField, FormDateField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DateFormatType } from 'api/Identity/Types/DateFormatType'
import { RoleDto } from 'api/Identity/Types/Role'
import { UserInTeamDto } from 'api/Identity/Types/UserTeamDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'
import { zonedTimeToUtc } from 'utils/time-zone/helpers'

export interface EditUserAutoAssignmentFormValues {
  isAutoAssignmentPaused: boolean
  autoAssignmentPausedStart: string | null
  autoAssignmentPausedUntil: string | null
}

const EditUserAutoAssignmentFormUI: React.FC<
  FormikProps<EditUserAutoAssignmentFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, setFieldValue, values, onClose } =
    props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Auto Assignment</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormCheckboxField
              name='isAutoAssignmentPaused'
              label='Paused Auto Assignment'
              error={errors.isAutoAssignmentPaused}
              onChange={(value) => {
                if (!value) {
                  setFieldValue('autoAssignmentPausedStart', null)
                  setFieldValue('autoAssignmentPausedUntil', null)
                }
                setFieldValue('isAutoAssignmentPaused', value)
              }}
            />
            <FormDateField
              name='autoAssignmentPausedStart'
              label='Pause Start'
              dateFormatType='dd/MM/yyyy'
              isClearable
              disabled={!values.isAutoAssignmentPaused}
              onChange={(value) => {
                setFieldValue('autoAssignmentPausedStart', value ? value.toDateString() : null)
              }}
            />
            <FormDateField
              name='autoAssignmentPausedUntil'
              label='Pause Until'
              dateFormatType='dd/MM/yyyy'
              isClearable
              disabled={!values.isAutoAssignmentPaused}
              onChange={(value) => {
                setFieldValue('autoAssignmentPausedUntil', value ? value.toDateString() : null)
              }}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  user?: UserInTeamDto
  roles: RoleDto[]
  dateFormat: DateFormatType[]
  onSubmit(values: FormSubmitValues<EditUserAutoAssignmentFormValues>): Promise<void>
}

function dataToSubmit(
  values: EditUserAutoAssignmentFormValues
): FormSubmitValues<EditUserAutoAssignmentFormValues> {
  return {
    ...values,
    autoAssignmentPausedStart: values.autoAssignmentPausedStart
      ? zonedTimeToUtc(new Date(values.autoAssignmentPausedStart)).toISOString()
      : null,
    autoAssignmentPausedUntil: values.autoAssignmentPausedUntil
      ? zonedTimeToUtc(new Date(values.autoAssignmentPausedUntil)).toISOString()
      : null,
  }
}

export const EditUserAutoAssignmentForm = withFormik<OuterProps, EditUserAutoAssignmentFormValues>({
  mapPropsToValues: ({ user }) => {
    return {
      isAutoAssignmentPaused: user?.isPausedAutoAssignment || false,
      autoAssignmentPausedStart: user?.autoAssignmentPausedStart
        ? user.autoAssignmentPausedStart
        : null,
      autoAssignmentPausedUntil: user?.autoAssignmentPausedUntil
        ? user.autoAssignmentPausedUntil
        : null,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<EditUserAutoAssignmentFormValues> = {}
    return errors
  },
  enableReinitialize: true,
})(EditUserAutoAssignmentFormUI)
